import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import { api, DRIVE_LINKS } from "@/lib/site";
import { STATIC_NOTICES } from "@/lib/staticData";
import { Calendar, Award, Megaphone, Bell, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

const ICONS = { event: Calendar, result: Award, announcement: Megaphone, general: Bell };
const COLORS = {
  event: "bg-blue-50 text-blue-700 border-blue-100",
  result: "bg-green-50 text-green-700 border-green-100",
  announcement: "bg-gold/15 text-gold-dark border-gold/30",
  general: "bg-royal/5 text-royal border-royal/15",
};

/** Strip year ranges like "2025-26", "2025-2026", "(2025)" from text. */
function stripYears(text) {
  if (!text) return text;
  return text
    .replace(/\b20\d{2}\s*[-–]\s*\d{2,4}\b/g, "")
    .replace(/\bfor\s+the\s+academic\s+year\b/gi, "for the upcoming academic year")
    .replace(/\(20\d{2}\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export default function NoticesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/notices")
      .then((r) => { setItems(r.data); })
      .catch(() => { setItems(STATIC_NOTICES); })
      .finally(() => setLoading(false));
  }, []);

  const driveLinkFor = (category) => {
    if (category === "result") return DRIVE_LINKS.results;
    if (category === "announcement") return DRIVE_LINKS.admission;
    if (category === "event") return DRIVE_LINKS.gallery;
    return null;
  };

  const renderCard = (n, i) => {
    const Icon = ICONS[n.category] || Bell;
    const color = COLORS[n.category] || COLORS.general;
    const href = driveLinkFor(n.category);
    const cleanTitle = stripYears(n.title);
    const cleanBody = stripYears(n.body);
    const ctaLabel = {
      result: "Click here for result",
      announcement: "Click here for admission form",
      event: "Click here for images",
    }[n.category];

    const inner = (
      <div className="flex flex-col md:flex-row">
        {n.image_url && (
          <img src={n.image_url} alt={cleanTitle} className="md:w-56 w-full h-44 md:h-auto object-cover" />
        )}
        <div className="p-6 md:p-8 flex-1">
          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold tracking-widest uppercase border ${color}`}>
            <Icon size={12} /> {n.category}
          </span>
          <h2 className="font-serif-display text-2xl md:text-3xl font-bold text-royal mt-4 group-hover:text-gold transition-colors">{cleanTitle}</h2>
          <p className="mt-3 text-gray-700 leading-relaxed whitespace-pre-wrap">{cleanBody}</p>
          {href && (
            <span className="mt-5 inline-flex items-center gap-1.5 text-gold-dark text-xs font-semibold tracking-wide hover:underline">
              {ctaLabel} <ExternalLink size={12} />
            </span>
          )}
        </div>
      </div>
    );

    return href ? (
      <motion.a
        key={n.id}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ delay: i * 0.06 }}
        data-testid={`notice-${i}`}
        className="group block bg-white rounded-2xl border border-gray-100 shadow-sm hover-lift overflow-hidden cursor-pointer"
      >
        {inner}
      </motion.a>
    ) : (
      <motion.article
        key={n.id}
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ delay: i * 0.06 }}
        data-testid={`notice-${i}`}
        className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover-lift overflow-hidden"
      >
        {inner}
      </motion.article>
    );
  };

  return (
    <div data-testid="notices-page">
      <PageHero eyebrow="NOTICES & ANNOUNCEMENTS" title="School Notice Board" subtitle="Latest school events, exam results, announcements and important updates for parents and students." />

      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto space-y-5">
          {loading && <p className="text-center text-gray-500 py-10">Loading notices...</p>}
          {!loading && items.length === 0 && <p className="text-center text-gray-500 py-10">No notices yet. Check back soon!</p>}
          {items.map(renderCard)}
        </div>
      </section>
    </div>
  );
}
